import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { Router } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import { DynamicFilterConfig, EnumValues, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { BankAccountProxyService, BankAccountSearchCommand } from '../../../proxies/bank-account.proxy.service';
import { BankAccount, BankAccountStatus, Currency } from '../../../models/bank-account/bank-account';
import { EnumService } from '../../../services/enum.service';

@Component({
  selector: 'app-bank-account',
  templateUrl: './bank-account.component.html',
  styleUrl: './bank-account.component.scss',
})
export class BankAccountComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly bankAccountService = inject(BankAccountProxyService);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);

  isLoading = signal(0);
  /** Rows for the current page — server-filtered and server-paged. */
  accounts = signal<BankAccount[]>([]);
  /** Every account, loaded once. Only feeds the KPI cards, which are deliberately filter-independent. */
  private readonly allAccounts = signal<BankAccount[]>([]);

  // ---- filters ----
  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'bank-account-filters', config: this.filterConfig });
  private lastSearchCriteria: BankAccountSearchCommand = {};

  private typeValues = signal<EnumValues>({});
  private statusValues = signal<EnumValues>({});
  private currencyValues = signal<EnumValues>({});
  enumsReady = signal(false);

  // ---- pagination / sorting ----
  readonly totalRecords = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);
  readonly sortField = signal<string | undefined>(undefined);
  readonly sortOrder = signal<number>(-1);

  readonly firstRecord = computed(() => (this.totalRecords() ? (this.currentPage() - 1) * this.pageSize() + 1 : 0));
  readonly lastRecord = computed(() => Math.min(this.currentPage() * this.pageSize(), this.totalRecords()));

  // ---- KPI cards (computed over ALL accounts, independent of filters) ----
  // KPI cards. Cash totals cover only the accounts this user may see the balance of — a redacted
  // balance is unknown, not zero, and folding it in as zero would understate the company's cash
  // without saying so. hiddenCount is displayed alongside so a partial total never reads as a full one.
  readonly totalCashUsd = computed(() => this.sumBalance(Currency.USD));
  readonly totalCashHnl = computed(() => this.sumBalance(Currency.HNL));
  readonly usdCount = computed(() => this.visibleBalanceAccounts().filter((a) => a.currency === Currency.USD).length);
  readonly hnlCount = computed(() => this.visibleBalanceAccounts().filter((a) => a.currency === Currency.HNL).length);
  readonly totalCount = computed(() => this.allAccounts().length);
  readonly activeCount = computed(() => this.allAccounts().filter((a) => a.status === BankAccountStatus.ACTIVE).length);
  readonly belowMinimumCount = computed(() => this.allAccounts().filter((a) => this.isBelowMinimum(a)).length);

  /** Accounts whose balance the API actually returned. */
  private readonly visibleBalanceAccounts = computed(() => this.allAccounts().filter((a) => a.canViewBalance && a.currentBalance !== null));

  /** How many accounts the cash totals could not include. Drives the "N hidden" hint on the cards. */
  readonly hiddenBalanceCount = computed(() => this.allAccounts().length - this.visibleBalanceAccounts().length);

  constructor() {
    this.filterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchWithFilters(filters as BankAccountSearchCommand));

    effect(
      () => {
        if (!this.enumService.enumsReady()) return;
        this.typeValues.set(this.enumService.getEnum('BankAccountType').values);
        this.statusValues.set(this.enumService.getEnum('BankAccountStatus').values);
        this.currencyValues.set(this.enumService.getEnum('Currency').values);
        this.enumsReady.set(true);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.initializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadAllForKpis();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'code',
          label: translate('BankAccount_Table_Code', 'Code'),
          placeholder: translate('BankAccount_Filter_Code', 'Enter code'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'name',
          label: translate('BankAccount_Table_Name', 'Account'),
          placeholder: translate('BankAccount_Filter_Name', 'Enter account name'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'bankName',
          label: translate('BankAccount_Table_BankName', 'Bank'),
          placeholder: translate('BankAccount_Filter_BankName', 'Enter bank'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'bankAccountNumber',
          label: translate('BankAccount_Table_AccountNumber', 'Account number'),
          placeholder: translate('BankAccount_Filter_AccountNumber', 'Enter account number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'currencies',
          label: translate('BankAccount_Table_Currency', 'Currency'),
          placeholder: translate('BankAccount_Filter_Currency', 'Select currencies'),
          options: this.currencyValues(),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Status', 'Status'),
          placeholder: translate('BankAccount_Filter_Status', 'Select statuses'),
          options: this.statusValues(),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'types',
          label: translate('BankAccount_Table_Type', 'Type'),
          placeholder: translate('BankAccount_Filter_Type', 'Select types'),
          options: this.typeValues(),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  /** KPI source. Separate from the grid query on purpose: the cards summarise the whole portfolio,
   *  so they must not shrink when someone filters the table down to one bank. */
  private loadAllForKpis(): void {
    this.startLoading();
    this.bankAccountService
      .getAll()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.allAccounts.set(response.data ?? []),
        error: () => this.allAccounts.set([]),
      });
  }

  private onSearch(searchCriteria: BankAccountSearchCommand): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.bankAccountService
      .search(searchCriteria, this.currentPage(), this.pageSize(), false, this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.accounts.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: (error) => {
          console.error('Error searching bank accounts:', error);
          this.accounts.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: BankAccountSearchCommand): void {
    const searchCriteria: BankAccountSearchCommand = {
      ...filters,
      // The filter component hands back option values as-is; the API binds enum lists, not strings.
      types: filters.types?.map(Number),
      currencies: filters.currencies?.map(Number),
      statuses: filters.statuses?.map(Number),
    };
    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field;
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) return;

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private sumBalance(currency: number): number {
    return this.visibleBalanceAccounts()
      .filter((a) => a.currency === currency)
      .reduce((total, a) => total + (a.currentBalance as number), 0);
  }

  isBelowMinimum(account: BankAccount): boolean {
    // Unknowable when either figure is redacted. Answering false is the honest option: the alternative
    // flags an account as short on cash on the strength of a number nobody was shown.
    if (account.currentBalance === null || account.minimumBalance === null) return false;
    return account.status === BankAccountStatus.ACTIVE && account.currentBalance < account.minimumBalance;
  }

  redirectToDetails(id?: string): void {
    this.router.navigate(['/bank-account-details'], { queryParams: { id: id ?? 'new' } });
  }

  typeLabel(type: number): string {
    return this.typeValues()[type] ?? '';
  }

  statusLabel(status: number): string {
    return this.statusValues()[status] ?? '';
  }

  currencyLabel(currency: number): string {
    return this.currencyValues()[currency] ?? '';
  }

  statusSeverity(status: number): 'success' | 'warn' | 'secondary' | 'info' {
    switch (status) {
      case BankAccountStatus.ACTIVE:
        return 'success';
      case BankAccountStatus.BLOCKED:
        return 'warn';
      case BankAccountStatus.INACTIVE:
        return 'secondary';
      default:
        return 'info';
    }
  }
}
